import { isAddressEqual } from "@opensea/utils"
import { proxy, subscribe } from "valtio"
import { deepClone } from "valtio/utils"
import type { WalletDestination } from "../components/DestinationWalletSelect"
import type { Address } from "../types"
import { persistStore, restoreStore } from "../utils/storeUtils"

// -- Helpers -------------------------------------------------------
const STORAGE_KEY = "opensea-wallet-destination-addresses-store"
const STORAGE_VERSION = 1
const MAX_CUSTOM_DESTINATION_ADDRESSES = 5

// -- Types ---------------------------------------------------------
export type CustomDestinationWallet = {
  identifier: string
  address: Address
}

type CustomDestinationWalletsStateType = {
  storageVersion: typeof STORAGE_VERSION
  destinationWallets: CustomDestinationWallet[]
  lastSelectedWallet?: WalletDestination
}

// -- State ---------------------------------------------------------
const INITIAL_STATE: CustomDestinationWalletsStateType = {
  storageVersion: STORAGE_VERSION,
  destinationWallets: [],
  lastSelectedWallet: undefined,
}

export const CustomDestinationWalletsState =
  proxy<CustomDestinationWalletsStateType>(
    deepClone(restoreStore(STORAGE_KEY, STORAGE_VERSION, INITIAL_STATE)),
  )

// -- Actions -------------------------------------------------------
export const CustomDestinationWalletsActions = {
  addDestinationWallet(wallet: CustomDestinationWallet) {
    const unique = CustomDestinationWalletsState.destinationWallets.filter(
      w => !isAddressEqual(w.address, wallet.address),
    )
    unique.unshift(wallet)
    const newWallets = unique.slice(0, MAX_CUSTOM_DESTINATION_ADDRESSES)
    CustomDestinationWalletsState.destinationWallets = newWallets
  },

  deleteDestinationWallet(address: Address) {
    const newWallets = CustomDestinationWalletsState.destinationWallets.filter(
      d => !isAddressEqual(d.address, address),
    )
    CustomDestinationWalletsState.destinationWallets = newWallets
    const lastAddress =
      CustomDestinationWalletsState.lastSelectedWallet?.address
    if (isAddressEqual(lastAddress, address)) {
      CustomDestinationWalletsState.lastSelectedWallet = undefined
    }
  },

  setLastSelectedWallet(wallet?: WalletDestination) {
    CustomDestinationWalletsState.lastSelectedWallet = wallet
  },
}

// -- Subscribe -----------------------------------------------------
subscribe(CustomDestinationWalletsState, () => {
  persistStore(STORAGE_KEY, CustomDestinationWalletsState)
})
